import { Link } from '@tanstack/react-router';
import type { FlattenedArticleItem } from '@/types/article-collection';
import { findAdjacentArticles, getArticlePositionInfo } from '@/utils/collectionTreeUtils';
import ArrowLeftIcon from '@/assets/common/arrow-left.svg?react';
import ArrowRightIcon from '@/assets/common/arrow-right.svg?react';
import styles from './CollectionReadingNav.module.scss';

interface CollectionReadingNavProps {
  flattenedArticles: FlattenedArticleItem[];
  currentArticleId: string;
  rootCollectionId: string;
}

export function CollectionReadingNav({
  flattenedArticles,
  currentArticleId,
  rootCollectionId,
}: CollectionReadingNavProps) {
  const { prev, next } = findAdjacentArticles(flattenedArticles, currentArticleId);
  const positionInfo = getArticlePositionInfo(flattenedArticles, currentArticleId);

  return (
    <nav className={styles.nav}>
      {prev ? (
        <Link
          to="/articles/collections/$collectionId/articles/$articleId"
          params={{
            collectionId: rootCollectionId,
            articleId: prev.article_id,
          }}
          className={styles.navButton}
        >
          <span className={styles.navButton__label}>
            <ArrowLeftIcon />
            Previous
          </span>
          <span className={styles.navButton__title}>{prev.title}</span>
          {prev.collection_name && (
            <span className={styles.navButton__collection}>{prev.collection_name}</span>
          )}
        </Link>
      ) : (
        <div className={`${styles.navButton} ${styles['navButton--disabled']}`}>
          <span className={styles.navButton__label}>
            <ArrowLeftIcon />
            Previous
          </span>
          <span className={styles.navButton__title}>No previous article</span>
        </div>
      )}

      {positionInfo && (
        <div className={styles.position}>
          {positionInfo.current} / {positionInfo.total}
        </div>
      )}

      {next ? (
        <Link
          to="/articles/collections/$collectionId/articles/$articleId"
          params={{
            collectionId: rootCollectionId,
            articleId: next.article_id,
          }}
          className={`${styles.navButton} ${styles['navButton--next']}`}
        >
          <span className={styles.navButton__label}>
            Next
            <ArrowRightIcon />
          </span>
          <span className={styles.navButton__title}>{next.title}</span>
          {next.collection_name && (
            <span className={styles.navButton__collection}>{next.collection_name}</span>
          )}
        </Link>
      ) : (
        <div
          className={`${styles.navButton} ${styles['navButton--next']} ${styles['navButton--disabled']}`}
        >
          <span className={styles.navButton__label}>
            Next
            <ArrowRightIcon />
          </span>
          <span className={styles.navButton__title}>No more articles</span>
        </div>
      )}
    </nav>
  );
}
